import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// --- Firebase Config ---
// This configuration now points to your production-ready Firebase project.
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Explicitly set the region to match where functions are deployed.
// Without this, the SDK may resolve to a wrong URL (e.g., 'us1-' instead of 'us-central1-'),
// causing CORS preflight failures on all Cloud Function calls.
export const functions = getFunctions(app, 'us-central1');

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'simulate2nl';

const CLOUD_RUN_CALLABLE_URLS: Record<string, string> = {
    adminListUsers: 'https://adminlistusers-vhtfexvyrq-uc.a.run.app',
    adminUserAction: 'https://adminuseraction-vhtfexvyrq-uc.a.run.app',
    adminUpdateUserAllocation: 'https://adminupdateuserallocation-vhtfexvyrq-uc.a.run.app',
    adminDataAssistant: 'https://admindataassistant-vhtfexvyrq-uc.a.run.app',
    parseRubricFile: 'https://parserubricfile-vhtfexvyrq-uc.a.run.app',
    geminiProxy: 'https://geminiproxy-vhtfexvyrq-uc.a.run.app',
    getVertexAuthToken: 'https://getvertexauthtoken-vhtfexvyrq-uc.a.run.app',
    getApiKeyForLive: 'https://getapikeyforlive-vhtfexvyrq-uc.a.run.app',
};

export interface AdminUserRecord {
    uid: string;
    email: string;
    fullName: string;
    team: string;
    lastSignInTime: string;
    creationTime: string;
    isAdmin?: boolean;
    allocatedMinutes?: number | 'unlimited';
    usedMinutes?: Record<string, number>;
    conversationGuideDisabled?: boolean;
}

/**
 * Calls a Firebase Callable Cloud Function with multi-endpoint fallback:
 * 1. Same-origin `/api/${functionName}` on Firebase Hosting (bypasses corporate firewall/Zscaler blocks on `*.cloudfunctions.net` and `*.a.run.app` with zero CORS preflight).
 * 2. Direct Cloud Functions URL (`https://us-central1-simulate2nl.cloudfunctions.net/${functionName}`).
 * 3. Direct Cloud Run URL (`https://<service>-vhtfexvyrq-uc.a.run.app`).
 */
export async function callFunctionWithFallback<TData = any, TResult = any>(
    functionName: string,
    payload?: TData,
    options?: { timeoutMs?: number; preferDirect?: boolean }
): Promise<{ data: TResult }> {
    const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    const directFunctionUrl = `https://us-central1-${PROJECT_ID}.cloudfunctions.net/${functionName}`;
    const cloudRunUrl = CLOUD_RUN_CALLABLE_URLS[functionName];
    const sameOriginUrl = !isLocalhost ? `/api/${functionName}` : null;

    const endpoints = (
        options?.preferDirect
            ? [directFunctionUrl, cloudRunUrl, sameOriginUrl]
            : [sameOriginUrl, directFunctionUrl, cloudRunUrl]
    ).filter(Boolean) as string[];

    const timeoutMs = options?.timeoutMs || 70_000;
    let lastError: any = null;

    for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }

        const currentUser = auth.currentUser;
        const idToken = currentUser ? await currentUser.getIdToken(attempt > 0) : null;

        for (const endpoint of endpoints) {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);

            try {
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                };
                if (idToken) {
                    headers['Authorization'] = `Bearer ${idToken}`;
                }

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ data: payload ?? null }),
                    signal: controller.signal,
                });

                clearTimeout(timer);

                let json: any = null;
                try {
                    json = await response.json();
                } catch {
                    json = null;
                }

                if (json?.error) {
                    const errStatus = String(json.error.status || '');
                    const errMsg = String(json.error.message || errStatus || 'Request failed');
                    const errObj: any = new Error(errMsg);
                    errObj.code = errStatus.toLowerCase();
                    errObj.details = json.error.details;

                    // Do not retry permanent 4xx errors across other endpoints
                    if (
                        errStatus === 'PERMISSION_DENIED' ||
                        errStatus === 'UNAUTHENTICATED' ||
                        errStatus === 'INVALID_ARGUMENT' ||
                        errStatus === 'NOT_FOUND'
                    ) {
                        throw errObj;
                    }
                    lastError = errObj;
                    continue;
                }

                if (response.ok && json && ('result' in json || 'data' in json)) {
                    const resultData = json.result !== undefined ? json.result : json.data;
                    return { data: resultData as TResult };
                }

                lastError = new Error(
                    `Service ${functionName} returned HTTP ${response.status} from ${endpoint}`
                );
            } catch (err: any) {
                clearTimeout(timer);
                if (
                    err?.code === 'permission_denied' ||
                    err?.code === 'unauthenticated' ||
                    err?.code === 'invalid_argument'
                ) {
                    throw err;
                }
                lastError = err;
                console.warn(`[callFunctionWithFallback] ${endpoint} failed (attempt ${attempt + 1}):`, err?.message || err);
            }
        }
    }

    // Final fallback to native httpsCallable
    try {
        const callable = httpsCallable<TData, TResult>(functions, functionName, { timeout: timeoutMs });
        const res = await callable(payload as TData);
        return { data: res.data };
    } catch (err: any) {
        throw lastError || err;
    }
}

/**
 * Fetches all users for Admin User Management & Export filters with 4-layer resilience:
 * 1. `/api/adminListUsers` (same-origin via Firebase Hosting rewrite)
 * 2. `https://us-central1-simulate2nl.cloudfunctions.net/adminListUsers`
 * 3. `https://adminlistusers-vhtfexvyrq-uc.a.run.app`
 * 4. Direct Firestore `collection(db, 'users')` fallback if all HTTP function endpoints are blocked by a corporate proxy/firewall.
 */
export async function fetchAdminUsersWithFallback(): Promise<AdminUserRecord[]> {
    try {
        const res = await callFunctionWithFallback<undefined, AdminUserRecord[]>('adminListUsers');
        if (Array.isArray(res.data) && res.data.length > 0) {
            return res.data;
        }
    } catch (callableErr: any) {
        console.warn('[fetchAdminUsersWithFallback] Callable endpoints failed, falling back to direct Firestore query:', callableErr?.message || callableErr);
    }

    // Layer 4: Direct Firestore fallback using existing authenticated Firestore channel
    const snap = await getDocs(collection(db, 'users'));
    const fallbackUsers: AdminUserRecord[] = snap.docs.map(docSnap => {
        const d = docSnap.data() as Record<string, any>;
        const rawUsed = d.usedMinutes;
        const sanitizedUsed: Record<string, number> = {};
        if (rawUsed && typeof rawUsed === 'object') {
            for (const [mKey, val] of Object.entries(rawUsed)) {
                const n = Number(val);
                if (Number.isFinite(n)) {
                    sanitizedUsed[mKey] = Math.round(n * 100) / 100;
                }
            }
        }
        return {
            uid: docSnap.id,
            email: d.email || '',
            fullName: d.fullName || d.displayName || '',
            team: d.teamName || d.team || '',
            lastSignInTime: d.lastLogin || d.lastSignInTime || '',
            creationTime: d.createdAt?.toDate ? d.createdAt.toDate().toISOString() : '',
            isAdmin: d.isAdmin === true,
            allocatedMinutes: d.allocatedMinutes ?? 150,
            usedMinutes: sanitizedUsed,
            conversationGuideDisabled: d.conversationGuideDisabled === true,
        };
    });

    return fallbackUsers;
}

// --- Secure Backend Function References ---
// Keep in sync with `timeoutSeconds` on the geminiProxy Cloud Function.
export const GEMINI_PROXY_TIMEOUT_MS = 540_000;
export const geminiProxy = (data: any) =>
    callFunctionWithFallback('geminiProxy', data, { timeoutMs: GEMINI_PROXY_TIMEOUT_MS, preferDirect: true });
export const getApiKeyForLive = (data?: any) =>
    callFunctionWithFallback('getApiKeyForLive', data);
export const getVertexAuthToken = (data?: any) =>
    callFunctionWithFallback('getVertexAuthToken', data);
